/**
 * Strategy (2026-09-29): company objectives, how they're measured, and which
 * portfolios, programs and projects deliver them. Replaces the static
 * "Strategy module" design shell (invented KPIs, "coming soon" tabs).
 * Backend: app/services/strategy.py over the Part 9 OKR tables.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2, Plus, Target, Trash2, X } from 'lucide-react'

import { Button } from '../components/ui/button'
import { useAuth } from '../contexts/AuthContext'
import { governanceService } from '../services/governance'
import { portfoliosService } from '../services/portfolios'
import { programsService } from '../services/programs'
import { projectsService } from '../services/projects'
import {
  strategyService, type DeliveryType, type StrategyHealth, type StrategyKeyResult, type StrategyObjective,
  type StrategyOverview,
} from '../services/strategy'
import { apiErrorMessage } from '../utils/apiError'

const HEALTH: Record<StrategyHealth, { label: string; cls: string }> = {
  on_track: { label: 'On track', cls: 'bg-success/15 text-success' },
  at_risk: { label: 'At risk', cls: 'bg-warning/15 text-warning' },
  off_track: { label: 'Off track', cls: 'bg-destructive/15 text-destructive' },
  no_dates: { label: 'No dates set', cls: 'bg-muted text-muted-foreground' },
  achieved: { label: 'Achieved', cls: 'bg-success/15 text-success' },
  dropped: { label: 'Dropped', cls: 'bg-muted text-muted-foreground' },
}

type Options = Record<DeliveryType, Array<{ id: number; name: string }>>

const input = 'min-h-[44px] rounded-md border border-border bg-background px-3 text-sm text-foreground'

function fmt(v: number | null | undefined, unit: string | null): string {
  if (v === null || v === undefined) return '—'
  const n = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (!unit) return n
  return unit === '$' || unit === '€' || unit === '£' ? `${unit}${n}` : `${n}${unit === '%' ? '%' : ` ${unit}`}`
}

export default function StrategyPage() {
  const { user } = useAuth()
  const canEdit = Boolean(user?.is_superuser || user?.roles?.some((r) => r.name === 'Organization Owner'))
  const [data, setData] = useState<StrategyOverview | null>(null)
  const [options, setOptions] = useState<Options>({ portfolio: [], program: [], project: [] })
  const [showNew, setShowNew] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await strategyService.overview())
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load strategy.'))
    }
  }, [])

  useEffect(() => {
    load()
    Promise.all([portfoliosService.list(), programsService.list(), projectsService.list()])
      .then(([pf, pg, pj]) => setOptions({
        portfolio: pf.map((p) => ({ id: p.id, name: p.name })),
        program: pg.map((p) => ({ id: p.id, name: p.name })),
        project: pj.map((p) => ({ id: p.id, name: p.name })),
      }))
      .catch(() => { /* link pickers just stay empty */ })
  }, [load])

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true)
    try {
      const res = await fn()
      if (res && typeof res === 'object' && 'objectives' in (res as object)) setData(res as StrategyOverview)
      else await load()
      if (ok) toast.success(ok)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  if (!data) return <p className="p-6 text-sm text-muted-foreground">Loading strategy…</p>
  const { summary, alignment, objectives } = data
  const active = objectives.filter((o) => o.status === 'active')

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground"><Target className="h-6 w-6" /> Strategy</h1>
          <p className="text-sm text-muted-foreground">What the organization is trying to achieve, how it's measured, and which work delivers it.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowNew((v) => !v)} className="min-h-[44px] gap-2"><Plus className="h-4 w-4" /> New objective</Button>
        )}
      </div>

      <section aria-label="Summary" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Active objectives" value={String(summary.active)} />
        <Tile label="Average progress" value={summary.average_progress === null ? '—' : `${summary.average_progress}%`} />
        <Tile label="Needing attention" value={String((summary.by_health.at_risk ?? 0) + (summary.by_health.off_track ?? 0))}
          hint={`${summary.by_health.off_track ?? 0} off track · ${summary.by_health.at_risk ?? 0} at risk`} />
        <Tile label="Projects aligned to strategy" value={alignment.alignment_percent === null ? '—' : `${alignment.alignment_percent}%`}
          hint={`${alignment.aligned_projects} of ${alignment.open_projects} open projects`} />
      </section>

      {showNew && canEdit && <NewObjective busy={busy} onCancel={() => setShowNew(false)}
        onCreate={(body) => run(async () => { await governanceService.createObjective(body); setShowNew(false) }, 'Objective created.')} />}

      {objectives.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
          <h2 className="text-lg font-semibold text-foreground">No objectives yet</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Start with two or three things the organization must achieve this year. Give each a measurable key result,
            then link the portfolios, programs or projects that deliver it. Anything unlinked shows up below as unaligned work.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {objectives.map((o) => (
            <ObjectiveCard key={o.id} o={o} canEdit={canEdit} busy={busy} options={options} run={run} />
          ))}
        </div>
      )}

      {alignment.open_projects > 0 && (
        <section aria-label="Unaligned projects" className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-medium text-foreground">Work not linked to any objective ({alignment.unaligned.length})</h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Open projects that don't deliver an active objective, directly or through their program or portfolio.
            Either link them, or ask whether they should continue.
          </p>
          {alignment.unaligned.length === 0 ? (
            <p className="text-sm text-success">Every open project supports an objective.</p>
          ) : (
            <ul className="divide-y divide-border">
              {alignment.unaligned.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <Link to={`/project/${p.id}`} className="text-sm font-medium text-foreground underline-offset-2 hover:underline">{p.name}</Link>
                  {canEdit && active.length > 0 && (
                    <select aria-label={`Link ${p.name} to an objective`} className={input} value="" disabled={busy}
                      onChange={(e) => e.target.value && run(() => strategyService.link(Number(e.target.value), 'project', p.id), 'Linked.')}>
                      <option value="">Link to objective…</option>
                      {active.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                    </select>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function NewObjective({ busy, onCreate, onCancel }: {
  busy: boolean
  onCreate: (body: Parameters<typeof governanceService.createObjective>[0]) => void
  onCancel: () => void
}) {
  const year = new Date().getFullYear()
  const [f, setF] = useState({ title: '', description: '', start_date: `${year}-01-01`, target_date: `${year}-12-31`,
    kr_title: '', kr_unit: '', kr_start: '0', kr_target: '' })
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (f.title.trim().length < 3) return
    const hasKr = f.kr_title.trim() && f.kr_target !== ''
    onCreate({
      title: f.title.trim(), description: f.description.trim() || undefined,
      start_date: f.start_date || undefined, target_date: f.target_date || undefined,
      key_results: hasKr ? [{ title: f.kr_title.trim(), unit: f.kr_unit.trim() || null,
        start_value: Number(f.kr_start || 0), target_value: Number(f.kr_target), current_value: Number(f.kr_start || 0) }] : [],
    })
  }
  return (
    <form onSubmit={submit} aria-label="New objective" className="grid gap-3 rounded-xl border border-border bg-card p-5 sm:grid-cols-2">
      <label className="text-sm text-foreground sm:col-span-2">Objective
        <input className={`${input} mt-1 w-full`} value={f.title} placeholder="e.g. Cut infrastructure cost by 30%"
          onChange={(e) => setF({ ...f, title: e.target.value })} required />
      </label>
      <label className="text-sm text-foreground sm:col-span-2">Why it matters (optional)
        <textarea className={`${input} mt-1 min-h-[70px] w-full py-2`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </label>
      <label className="text-sm text-foreground">Starts
        <input type="date" className={`${input} mt-1 w-full`} value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} />
      </label>
      <label className="text-sm text-foreground">Achieve by
        <input type="date" className={`${input} mt-1 w-full`} value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} />
      </label>
      <fieldset className="grid gap-3 sm:col-span-2 sm:grid-cols-4">
        <legend className="mb-1 text-sm font-medium text-foreground">First key result (optional)</legend>
        <label className="text-xs text-foreground sm:col-span-2">Measure
          <input className={`${input} mt-1 w-full`} value={f.kr_title} placeholder="Monthly cloud bill" onChange={(e) => setF({ ...f, kr_title: e.target.value })} />
        </label>
        <label className="text-xs text-foreground">From
          <input type="number" className={`${input} mt-1 w-full`} value={f.kr_start} onChange={(e) => setF({ ...f, kr_start: e.target.value })} />
        </label>
        <label className="text-xs text-foreground">To
          <input type="number" className={`${input} mt-1 w-full`} value={f.kr_target} onChange={(e) => setF({ ...f, kr_target: e.target.value })} />
        </label>
        <label className="text-xs text-foreground">Unit
          <input className={`${input} mt-1 w-full`} value={f.kr_unit} placeholder="%, $, users" onChange={(e) => setF({ ...f, kr_unit: e.target.value })} />
        </label>
      </fieldset>
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" disabled={busy} className="min-h-[44px]">Create objective</Button>
        <Button type="button" variant="outline" onClick={onCancel} className="min-h-[44px]">Cancel</Button>
      </div>
    </form>
  )
}

function ObjectiveCard({ o, canEdit, busy, options, run }: {
  o: StrategyObjective
  canEdit: boolean
  busy: boolean
  options: Options
  run: (fn: () => Promise<unknown>, ok?: string) => Promise<void>
}) {
  const [linkType, setLinkType] = useState<DeliveryType>('project')
  const [newKr, setNewKr] = useState({ title: '', start: '0', target: '', unit: '' })
  const h = HEALTH[o.health]
  const linkedIds = new Set(o.delivered_by.filter((d) => d.type === linkType).map((d) => d.id))

  return (
    <article aria-label={o.title} className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-foreground">{o.title}</h2>
          {o.description && <p className="mt-1 text-sm text-muted-foreground">{o.description}</p>}
          <p className="mt-1 text-xs text-muted-foreground">
            {o.start_date && o.target_date ? `${o.start_date} → ${o.target_date}` : o.period ?? 'No dates set'}
            {o.owner_name ? ` · owner ${o.owner_name}` : ''}
          </p>
        </div>
        <div className="text-right">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${h.cls}`}>{h.label}</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{o.progress}%</p>
          {o.expected_progress !== null && o.status === 'active' && (
            <p className="text-xs text-muted-foreground">{o.expected_progress}% of the time gone</p>
          )}
        </div>
      </div>
      <Bar value={o.progress} marker={o.status === 'active' ? o.expected_progress : null} />

      <section aria-label={`Key results for ${o.title}`} className="mt-4 space-y-3">
        {o.key_results.length === 0 && <p className="text-sm text-muted-foreground">No key results yet. Add one so progress can be measured.</p>}
        {o.key_results.map((kr) => <KeyResultRow key={kr.id} kr={kr} busy={busy} canEdit={canEdit} run={run} />)}
        {canEdit && o.status === 'active' && (
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => {
            e.preventDefault()
            if (!newKr.title.trim() || newKr.target === '') return
            run(() => governanceService.addKeyResult(o.id, {
              title: newKr.title.trim(), unit: newKr.unit.trim() || null, start_value: Number(newKr.start || 0),
              target_value: Number(newKr.target), current_value: Number(newKr.start || 0),
            }), 'Key result added.').then(() => setNewKr({ title: '', start: '0', target: '', unit: '' }))
          }}>
            <input aria-label={`New key result on ${o.title}`} placeholder="Add a key result" className={`${input} min-w-[180px] flex-1`}
              value={newKr.title} onChange={(e) => setNewKr({ ...newKr, title: e.target.value })} />
            <input aria-label={`Starting value for new key result on ${o.title}`} type="number" className={`${input} w-24`} value={newKr.start} onChange={(e) => setNewKr({ ...newKr, start: e.target.value })} />
            <input aria-label={`Target for new key result on ${o.title}`} type="number" placeholder="Target" className={`${input} w-24`} value={newKr.target} onChange={(e) => setNewKr({ ...newKr, target: e.target.value })} />
            <input aria-label={`Unit for new key result on ${o.title}`} placeholder="Unit" className={`${input} w-20`} value={newKr.unit} onChange={(e) => setNewKr({ ...newKr, unit: e.target.value })} />
            <Button type="submit" variant="outline" className="min-h-[44px]" disabled={busy}>Add</Button>
          </form>
        )}
      </section>

      <section aria-label={`Work delivering ${o.title}`} className="mt-5">
        <h3 className="mb-2 text-sm font-medium text-foreground">Delivered by</h3>
        <div className="flex flex-wrap gap-2">
          {o.delivered_by.length === 0 && <span className="text-sm text-muted-foreground">Nothing linked yet.</span>}
          {o.delivered_by.map((d) => (
            <span key={d.link_id} className="flex items-center gap-1 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-foreground">
              <span className="text-muted-foreground">{d.type}</span>
              {d.type === 'project'
                ? <Link to={`/project/${d.id}`} className="font-medium hover:underline">{d.name}</Link>
                : <span className="font-medium">{d.name}</span>}
              {d.progress !== null && <span className="text-muted-foreground">· {d.progress}%</span>}
              {canEdit && (
                <button type="button" aria-label={`Unlink ${d.name}`} disabled={busy} className="ml-1 rounded p-0.5 hover:bg-muted"
                  onClick={() => run(() => strategyService.unlink(o.id, d.link_id))}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canEdit && (
          <div className="mt-3 flex flex-wrap gap-2">
            <select aria-label="Type of work to link" className={input} value={linkType}
              onChange={(e) => setLinkType(e.target.value as DeliveryType)}>
              <option value="project">Project</option>
              <option value="program">Program</option>
              <option value="portfolio">Portfolio</option>
            </select>
            <select aria-label={`Link a ${linkType}`} className={`${input} min-w-[200px] flex-1`} value="" disabled={busy}
              onChange={(e) => e.target.value && run(() => strategyService.link(o.id, linkType, Number(e.target.value)), 'Linked.')}>
              <option value="">Link a {linkType}…</option>
              {options[linkType].filter((x) => !linkedIds.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        )}
      </section>

      {canEdit && (
        <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
          {o.status === 'active' ? (
            <>
              <Button variant="outline" size="sm" className="min-h-[44px] gap-1" disabled={busy}
                onClick={() => run(() => governanceService.updateObjective(o.id, { status: 'achieved' }), 'Marked achieved.')}>
                <CheckCircle2 className="h-4 w-4" /> Mark achieved
              </Button>
              <Button variant="ghost" size="sm" className="min-h-[44px]" disabled={busy}
                onClick={() => run(() => governanceService.updateObjective(o.id, { status: 'abandoned' }), 'Objective dropped.')}>
                Drop
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" className="min-h-[44px]" disabled={busy}
              onClick={() => run(() => governanceService.updateObjective(o.id, { status: 'active' }), 'Reopened.')}>
              Reopen
            </Button>
          )}
          <Button variant="ghost" size="sm" className="min-h-[44px] gap-1 text-destructive" disabled={busy}
            onClick={() => { if (confirm(`Delete "${o.title}" and its key results?`)) run(() => governanceService.deleteObjective(o.id), 'Deleted.') }}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      )}
    </article>
  )
}

function Bar({ value, marker }: { value: number; marker?: number | null }) {
  return (
    <div className="relative mt-3 h-2 w-full rounded-full bg-muted" aria-hidden>
      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, value)}%` }} />
      {marker !== null && marker !== undefined && (
        <div className="absolute -top-1 h-4 w-0.5 bg-foreground/60" style={{ left: `${Math.min(100, marker)}%` }} title="Where it should be by now" />
      )}
    </div>
  )
}

function KeyResultRow({ kr, busy, canEdit, run }: {
  kr: StrategyKeyResult
  busy: boolean
  canEdit: boolean
  run: (fn: () => Promise<unknown>, ok?: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const last = kr.checkins.length ? kr.checkins[kr.checkins.length - 1] : null
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{kr.title}</span>
        <span className="text-sm text-muted-foreground">
          {fmt(kr.current_value, kr.unit)} <span className="text-xs">of {fmt(kr.target_value, kr.unit)} (from {fmt(kr.start_value, kr.unit)})</span>
        </span>
      </div>
      <Bar value={kr.progress} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{kr.progress}%{last ? ` · last update ${last.at.slice(0, 10)}${last.by ? ` by ${last.by}` : ''}${last.note ? ` — ${last.note}` : ''}` : ' · no updates yet'}</span>
        <span className="flex gap-1">
          <button type="button" className="min-h-[32px] rounded px-2 text-primary hover:underline" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Update value'}
          </button>
          {canEdit && (
            <button type="button" aria-label={`Delete ${kr.title}`} className="min-h-[32px] rounded px-2 hover:text-destructive" disabled={busy}
              onClick={() => run(() => governanceService.deleteKeyResult(kr.id))}>
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </span>
      </div>
      {open && (
        <form className="mt-2 flex flex-wrap gap-2" onSubmit={(e) => {
          e.preventDefault()
          if (value === '' || Number.isNaN(Number(value))) return
          run(() => strategyService.checkin(kr.id, Number(value), note || undefined), 'Progress updated.')
            .then(() => { setOpen(false); setValue(''); setNote('') })
        }}>
          <input aria-label={`New value for ${kr.title}`} type="number" step="any" className={`${input} w-32`} value={value}
            onChange={(e) => setValue(e.target.value)} required />
          <input aria-label="What changed" placeholder="What changed? (optional)" className={`${input} min-w-[180px] flex-1`} value={note}
            onChange={(e) => setNote(e.target.value)} />
          <Button type="submit" className="min-h-[44px]" disabled={busy}>Save</Button>
        </form>
      )}
    </div>
  )
}
